import { useMemo, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { useDash } from "./ctx";
import { PulseChip } from "@/components/pulse/PulseChip";
import { usePulseBadge } from "@/components/pulse/usePulseBadge";
import { InviteFlyerDialog } from "@/components/InviteFlyer";
import { ManagedBy, managerOptions, matchesManager, managerSortKey } from "@/components/ManagedBy";
import { CommunityCard } from "@/components/CommunityCard";
import { CommunityMark } from "@/components/CommunityMark";
import { makeVocab, unitWord, V } from "@/lib/vocab";
import {
  Building2, MessagesSquare, Phone, AlertTriangle, Users, ArrowRight, Search, Flag,
  UserRound, Megaphone, MessageSquareText, SlidersHorizontal, LayoutGrid, List, ChevronDown, Headset, ShieldAlert,
} from "lucide-react";

// Portfolio-scoped communities browser for management-company admins — same card/list
// experience the super admin gets, minus platform-only controls (delete, Twilio, wizard).
function ModulePill({ on, icon: Icon, label }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${on ? "bg-emerald-50 text-emerald-700" : "bg-sand-100 text-ink-300 line-through decoration-1"}`}>
      <Icon className="h-3 w-3" strokeWidth={2} /> {label}
    </span>
  );
}

export default function PortfolioCommunities() {
  const { tenants, enterCommunity, refreshTenants, role, viewCompanyId } = useDash();
  const [pulseSnaps, reloadPulse] = usePulseBadge(viewCompanyId);
  const pulseChip = (t) => <PulseChip tenant={t} snap={pulseSnaps[t.id]} role={role} onOpen={() => enterCommunity(t.id, "/dashboard/pulse")} onTurnedOn={async () => { await refreshTenants(); reloadPulse(); }} />;
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("name");
  const [manager, setManager] = useState("all");
  const managers = useMemo(() => managerOptions(tenants), [tenants]);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [view, setViewState] = useState(() => {
    try { return localStorage.getItem("askredline.portfolioCommunitiesView") || "cards"; } catch { return "cards"; }
  });
  const setView = (v) => { setViewState(v); try { localStorage.setItem("askredline.portfolioCommunitiesView", v); } catch { /* ignore */ } };
  const [expanded, setExpanded] = useState({});
  const [poster, setPoster] = useState(null);
  const toggleCard = (id) => setExpanded((m) => ({ ...m, [id]: !m[id] }));

  const flaggedOf = (t) => (t.stats?.escalations || 0) + (t.stats?.urgent_requests || 0);
  const totals = useMemo(() => ({
    communities: tenants.length,
    texts: tenants.reduce((a, t) => a + (t.stats?.texts ?? t.stats?.conversations ?? 0), 0),
    calls: tenants.reduce((a, t) => a + (t.stats?.calls ?? 0), 0),
    flagged: tenants.reduce((a, t) => a + flaggedOf(t), 0),
  }), [tenants]);

  const list = useMemo(() => {
    let l = [...tenants];
    const s = q.trim().toLowerCase();
    if (s) l = l.filter((t) => (t.name || "").toLowerCase().includes(s) || (t.persona_name || "").toLowerCase().includes(s));
    if (flaggedOnly) l = l.filter((t) => flaggedOf(t) > 0);
    l = l.filter((t) => matchesManager(t, manager));
    if (sort === "name") l.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    if (sort === "activity") l.sort((a, b) => (b.stats?.conversations || 0) - (a.stats?.conversations || 0));
    if (sort === "attention") l.sort((a, b) => flaggedOf(b) - flaggedOf(a));
    if (sort === "manager") l.sort((a, b) => managerSortKey(a).localeCompare(managerSortKey(b)) || (a.name || "").localeCompare(b.name || ""));
    return l;
  }, [tenants, q, sort, flaggedOnly, manager]);

  return (
    <div className="space-y-6" data-testid="portfolio-communities-page">
      <div>
        <PageTitle title={<><Building2 className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> {V.many("org")}</>} about={`Every ${V.lc("org")} in your portfolio — drill into any of them to manage it.`} />
      </div>

      {/* Compact rollup */}
      <div data-testid="portfolio-communities-rollup" className="bg-white rounded-2xl border border-sand-200 px-5 py-3 flex flex-wrap items-center gap-x-7 gap-y-2">
        <div className="flex items-baseline gap-1.5">
          <span className="font-heading text-xl font-extrabold tracking-tight text-ink">{totals.communities}</span>
          <span className="text-xs text-ink/50 font-medium">{V.many("org")}</span>
        </div>
        <div className="flex items-baseline gap-1.5" data-testid="portfolio-rollup-texts">
          <span className="font-heading text-xl font-extrabold tracking-tight text-ink/70">{totals.texts}</span>
          <span className="text-xs text-ink/50 font-medium">Texts</span>
        </div>
        <div className="flex items-baseline gap-1.5" data-testid="portfolio-rollup-calls">
          <span className="font-heading text-xl font-extrabold tracking-tight text-ink/70">{totals.calls}</span>
          <span className="text-xs text-ink/50 font-medium">Calls</span>
        </div>
        <button onClick={() => setFlaggedOnly((v) => !v)} data-testid="portfolio-rollup-attention"
          className={`flex items-baseline gap-1.5 rounded-full px-3 py-1 -my-1 transition-colors ${totals.flagged > 0 ? "bg-redline-50 hover:bg-redline-100" : "hover:bg-sand-50"}`}>
          <span className={`font-heading text-xl font-extrabold tracking-tight flex items-center gap-1.5 ${totals.flagged > 0 ? "text-redline-600" : "text-ink/70"}`}>
            {totals.flagged}{totals.flagged > 0 && <Flag className="h-3.5 w-3.5" />}
          </span>
          <span className={`text-xs font-medium ${totals.flagged > 0 ? "text-redline-600" : "text-ink/50"}`}>Needs attention</span>
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="portfolio-communities-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${V.lcs("org")}…`}
            className="rounded-full bg-white border border-sand-200 pl-9 pr-4 py-2 text-sm outline-none focus:border-ink w-56" />
        </div>
        <select data-testid="portfolio-communities-sort" value={sort} onChange={(e) => setSort(e.target.value)}
          className="rounded-full bg-white border border-sand-200 px-3.5 py-2 text-sm font-semibold text-ink/70 outline-none">
          <option value="name">Sort: Name</option>
          <option value="activity">Sort: Most active</option>
          <option value="attention">Sort: Needs attention</option>
          <option value="manager">Sort: Manager</option>
        </select>
        <select data-testid="portfolio-communities-manager" value={manager} onChange={(e) => setManager(e.target.value)}
          className={`rounded-full bg-white border px-3.5 py-2 text-sm font-semibold outline-none ${manager !== "all" ? "border-ink text-ink" : "border-sand-200 text-ink/70"}`}>
          <option value="all">Manager: All</option>
          {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          <option value="none">Unassigned</option>
        </select>
        <div className="flex rounded-full border border-sand-200 bg-white p-0.5 ml-auto" data-testid="portfolio-view-toggle">
          <button data-testid="portfolio-view-cards-btn" onClick={() => setView("cards")} title="Card view"
            className={`h-8 w-9 rounded-full flex items-center justify-center transition-colors ${view === "cards" ? "bg-ink text-white" : "text-ink/50 hover:text-ink"}`}>
            <LayoutGrid className="h-4 w-4" strokeWidth={1.75} />
          </button>
          <button data-testid="portfolio-view-list-btn" onClick={() => setView("list")} title="List view"
            className={`h-8 w-9 rounded-full flex items-center justify-center transition-colors ${view === "list" ? "bg-ink text-white" : "text-ink/50 hover:text-ink"}`}>
            <List className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      {view === "list" ? (
        <div className="bg-white rounded-2xl border border-sand-200 divide-y divide-sand-100 overflow-hidden" data-testid="portfolio-communities-list">
          {list.map((t) => {
            const flagged = flaggedOf(t);
            const unit = unitWord(t);
            const vt = makeVocab(t);                 // this row's industry words (iter508)
            return (
              <div key={t.id} data-testid={`portfolio-community-row-${t.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-sand-50/70 transition-colors">
                <button onClick={() => enterCommunity(t.id)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                  <CommunityMark tenant={t} size={36} rounded="rounded-lg" flagged={flagged > 0} testid={`portfolio-community-row-mark-${t.id}`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-heading font-bold text-sm text-ink truncate">{t.name}</span>
                      {(t.stats?.urgent_requests || 0) > 0 && (
                        <span className="rounded-full bg-danger-600 text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide shrink-0">{t.stats.urgent_requests} urgent</span>
                      )}
                      {t.status === "pending_activation" && (
                        <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide shrink-0">Pending number</span>
                      )}
                    </div>
                    <div className="text-xs text-ink/50 truncate mt-0.5">
                      <span className="text-redline-600 font-semibold">{t.persona_name}</span>
                      {t.phone_number ? <span className="tabular-nums"> · {t.phone_number}</span> : " · No number yet"}
                    </div>
                    <ManagedBy tenant={t} testid={`portfolio-community-manager-${t.id}`} className="mt-0.5" />
                  </div>
                </button>
                <div className="hidden md:flex items-center gap-5 text-xs tabular-nums shrink-0">
                  <span className="flex items-center gap-1 text-ink/50 w-16 justify-end" title="Text conversations (all time)"><MessagesSquare className="h-3.5 w-3.5 text-ink/30" strokeWidth={1.75} /> {t.stats?.texts ?? t.stats?.conversations ?? 0}</span>
                  <span className="flex items-center gap-1 text-ink/50 w-14 justify-end" title="Phone calls (all time)"><Phone className="h-3.5 w-3.5 text-ink/30" strokeWidth={1.75} /> {t.stats?.calls ?? 0}</span>
                  <span className={`flex items-center gap-1 w-14 justify-end ${flagged > 0 ? "text-danger-600 font-bold" : "text-ink/50"}`}><AlertTriangle className={`h-3.5 w-3.5 ${flagged > 0 ? "text-danger-500" : "text-ink/30"}`} strokeWidth={1.75} /> {flagged}</span>
                  <span className="flex items-center gap-1 text-ink/50 w-16 justify-end" title={unit}><Users className="h-3.5 w-3.5 text-ink/30" strokeWidth={1.75} /> {t.stats?.households || 0}</span>
                  <span className="hidden md:inline-flex">{pulseChip(t)}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={`portfolio-invite-${t.id}`} onClick={() => setPoster(t)} title={`Invite ${vt.lcs("person")} — QR poster & opt-in link`}
                    className="h-8 w-8 rounded-lg text-ink/35 hover:text-ink hover:bg-sand-100 flex items-center justify-center transition-colors">
                    <Users className="h-4 w-4" strokeWidth={1.75} />
                  </button>
                  <button data-testid={`portfolio-manage-${t.id}`} onClick={() => enterCommunity(t.id)}
                    className="rounded-full bg-ink text-white hover:bg-redline-500 transition-colors font-semibold px-3.5 py-1.5 text-xs flex items-center gap-1 shrink-0">
                    Manage <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                  </button>
                </div>
              </div>
            );
          })}
          {list.length === 0 && <div className="text-center py-16 text-ink/40">No {V.lcs("org")} match your search / filter.</div>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((t) => {
            const bms = t.board_members || [];
            const vt = makeVocab(t);                 // this card's industry words (iter508)
            const isOpen = !!expanded[t.id];
            return (
              <CommunityCard key={t.id} tenant={t} enterCommunity={enterCommunity} pulseChip={pulseChip(t)} testid={`portfolio-community-card-${t.id}`}
                footer={(
                  <div className="mt-auto p-3 flex gap-2 border-t border-sand-100">
                    <button data-testid={`portfolio-manage-${t.id}`} onClick={() => enterCommunity(t.id)}
                      className="flex-1 rounded-xl bg-ink text-white hover:bg-redline-500 transition-colors font-semibold px-4 py-2 text-sm flex items-center justify-center gap-1.5 active:scale-[0.98]">
                      Manage {V.lc("org")} <ArrowRight className="h-4 w-4" strokeWidth={2} />
                    </button>
                    <button data-testid={`portfolio-expand-${t.id}`} onClick={() => toggleCard(t.id)} aria-expanded={isOpen}
                      title={isOpen ? "Hide details" : "Board members, modules & more"}
                      className="w-10 rounded-xl bg-white border border-sand-200 text-ink/50 hover:text-ink hover:border-sand-300 transition-colors flex items-center justify-center">
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} strokeWidth={2} />
                    </button>
                  </div>
                )}>
                {isOpen && (
                  <div className="border-t border-sand-100 px-4 py-3 space-y-3" data-testid={`portfolio-community-details-${t.id}`}>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-ink/40 mb-1.5">Board members</div>
                      {bms.length === 0 ? <div className="text-sm text-ink/40">None added yet</div> : (
                        <div className="space-y-1">
                          {bms.slice(0, 3).map((m, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              <UserRound className="h-3.5 w-3.5 text-ink/40 shrink-0" />
                              <span className="font-medium text-ink truncate">{m.name}</span>
                              {m.position && <span className="text-ink/50 truncate">· {m.position}</span>}
                            </div>
                          ))}
                          {bms.length > 3 && <div className="text-xs text-ink/50 pl-5">+{bms.length - 3} more</div>}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-ink/40 mb-1.5">Modules</div>
                      <div className="flex flex-wrap gap-1.5">
                        <ModulePill on icon={MessageSquareText} label="Concierge" />
                        <ModulePill on={(t.mode || "answers_requests") === "answers_requests"} icon={SlidersHorizontal} label="Requests" />
                        <ModulePill on={!!t.support_module?.enabled} icon={Headset} label="Concierge Team" />
                        <ModulePill on={!!t.security_module?.enabled} icon={ShieldAlert} label="Security" />
                        <ModulePill on={t.features?.voice !== false && !!t.phone_number} icon={Megaphone} label="Voice" />
                      </div>
                    </div>
                    <button data-testid={`portfolio-invite-${t.id}`} onClick={() => setPoster(t)}
                      className="w-full rounded-xl bg-white border border-sand-200 text-ink/80 hover:bg-sand-50 hover:border-sand-300 transition-colors px-3 py-2 text-sm font-semibold flex items-center justify-center gap-1.5">
                      <Users className="h-4 w-4" strokeWidth={1.75} /> Invite {vt.lcs("person")}
                    </button>
                  </div>
                )}
              </CommunityCard>
            );
          })}
          {list.length === 0 && <div className="col-span-full text-center py-16 text-ink/40">No {V.lcs("org")} match your search / filter.</div>}
        </div>
      )}
      <InviteFlyerDialog community={poster} onClose={() => setPoster(null)} />
    </div>
  );
}
